import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject, Subscription, combineLatest, interval } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { HeaderService } from '../../services/header.service';
import { LeadCardLegacyComponent, Lead } from './lead-card/lead-card.component';
import { LeadDetailModalLegacyComponent } from './lead-detail-modal/lead-detail-modal.component';
import { SelectIcpModalLegacyComponent } from './select-icp-modal/select-icp-modal.component';
import { CrmPaginationComponent } from '../../pages/crm/crm-pagination/crm-pagination.component';
import { DailyLead, IcpAdjustmentNotification, PaginationMeta } from './lead.model';
import { loadDailyLeads, loadDailyLeadDetail, clearDailyLeadDetail, generateDailyLeads, loadDailyLeadRuns, loadNotifications, markAllNotificationsRead } from '../../state/dailyLeadLegacy/daily-lead.actions';
import * as IcpActions from '../../state/icp/icp.actions';
import { selectIcps, selectIcpsLoading } from '../../state/icp/icp.selectors';
import { ICP } from '../../state/icp/icp.state';
import { newSearch } from '../../state/search/search.actions';
import { selectNewSearchSuccess, selectNewSearchFailure } from '../../state/search/search.selectors';
import { selectAuthUser } from '../../state/auth/auth.selectors';
import { Router, RouterModule } from '@angular/router';
import { SharedService } from '../../services/shared.service';
import { ReportBannerService } from '../../services/report-banner.service';
import { EventTypes } from '../../event-types';
import {
  selectDailyLeads,
  selectSelectedLead,
  selectDailyLeadLoading,
  selectDailyLeadDetailLoading,
  selectDailyLeadMeta,
  selectDailyLeadGenerating,
  selectDailyLeadError,
  selectDailyLeadEstimatedLeads,
  selectDailyLeadRuns,
  selectDailyLeadRunsLoading,
  selectNotifications,
  selectNotificationsMeta,
  selectNotificationsUnreadCount,
  selectMarkingAllRead,
} from '../../state/dailyLeadLegacy/daily-lead.selectors';
import { PopupService } from '../../services/popup.service';
import { LeadsLegacyService } from '../../services/leads-legacy.service';

type FilterTab = 'new' | 'all' | 'notifications';
type SortOption = 'newest' | 'oldest';

const AVATAR_COLORS = [
  'bg-violet-500', 'bg-green-600', 'bg-amber-500', 'bg-blue-600',
  'bg-orange-500', 'bg-rose-500', 'bg-teal-500', 'bg-indigo-500',
  'bg-cyan-500', 'bg-pink-500',
];

@Component({
  selector: 'app-leads-legacy',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, LeadCardLegacyComponent, LeadDetailModalLegacyComponent, SelectIcpModalLegacyComponent, CrmPaginationComponent],
  templateUrl: './leads-legacy.component.html',
  styleUrl: './leads-legacy.component.scss'
})
export class LeadsLegacyComponent implements OnInit, OnDestroy {
  activeFilter: FilterTab = 'new';
  generating = false;
  hasGenerated = false;
  sortBy: SortOption = 'newest';
  showDetailModal = false;
  showIcpModal = false;
  icps: ICP[] = [];
  icpsLoading = false;
  generatingProfileId: string | null = null;
  generatedReports: Map<string, string> = new Map(); // leadId → reportId
  selectedIcpId: number | null = null;
  icpAdjustmentNotification: IcpAdjustmentNotification | null = null;

  leads: Lead[] = [];
  selectedLead$: Observable<DailyLead | null>;
  detailLoading$: Observable<boolean>;
  loading = false;
  meta: PaginationMeta | null = null;
  runInProgress = false;
  runsChecked = false;
  notifications: IcpAdjustmentNotification[] = [];
  notificationsMeta: PaginationMeta | null = null;
  notificationsUnreadCount = 0;
  markingAllRead = false;

  private user: any = null;
  private destroy$ = new Subject<void>();
  private searchSuccessSub: Subscription | null = null;
  private searchFailureSub: Subscription | null = null;
  private generatingLinkedinUrl: string | null = null;

  constructor(
    private headerService: HeaderService,
    private store: Store,
    private sharedService: SharedService,
    private reportBannerService: ReportBannerService,
    private popupService: PopupService,
    private router: Router,
    private leadsService: LeadsLegacyService,
  ) {
    this.selectedLead$ = this.store.select(selectSelectedLead);
    this.detailLoading$ = this.store.select(selectDailyLeadDetailLoading);
  }

  ngOnInit(): void {
    this.headerService.setHeaderState('Leads', 'Review and contact your daily prospects', true);

    // Subscribe to state
    this.store.select(selectDailyLeadLoading).pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.loading = loading;
    });

    this.store.select(selectDailyLeads).pipe(takeUntil(this.destroy$)).subscribe(dailyLeads => {
      this.leads = (dailyLeads || []).map((dl, index) => this.mapDailyLeadToLead(dl, index));
    });

    this.store.select(selectDailyLeadMeta).pipe(takeUntil(this.destroy$)).subscribe(meta => {
      this.meta = meta;
    });

    this.store.select(selectDailyLeadGenerating).pipe(takeUntil(this.destroy$)).subscribe(generating => {
      if (this.generating && !generating) {
        this.hasGenerated = true;
      }
      this.generating = generating;
    });

    this.store.select(selectDailyLeadError).pipe(
      takeUntil(this.destroy$),
      filter(error => error !== null),
    ).subscribe(error => {
      const body = error?.error;
      const status = error?.status || body?.status;

      // Prefer the API's descriptive message when available
      const apiMessage = body?.message || body?.error;

      let message = 'Something went wrong while generating leads. Please try again.';
      let type: 'error' | 'info' | 'warning' = 'error';

      if (status === 402) {
        message = apiMessage || 'Insufficient credits. Please upgrade your plan.';
      } else if (status === 409) {
        message = apiMessage || 'A lead generation run is already in progress. Please wait.';
        type = 'info';
      } else if (status === 429) {
        message = apiMessage || 'Rate limit reached. Please wait before trying again.';
        type = 'info';
      } else if (status === 400) {
        message = apiMessage || 'Invalid request. Please check your ICP configuration.';
      } else if (apiMessage) {
        message = apiMessage;
      }

      this.popupService.notify(message, 'Lead Generation', type);
      this.hasGenerated = false;
    });

    this.store.select(selectDailyLeadEstimatedLeads).pipe(
      takeUntil(this.destroy$),
      filter(count => count !== null && count === 0),
    ).subscribe(() => {
      this.popupService.notify(
        'No leads could be generated. Your ICP criteria may be too restrictive. Try adjusting company size, industry, or location filters.',
        'No Leads Found',
        'warning'
      );
      this.leadsService.getIcpAdjustmentNotification().pipe(take(1)).subscribe(n => {
        this.icpAdjustmentNotification = n;
      });
    });

    // Check latest run first, then load leads only if complete
    this.store.dispatch(loadDailyLeadRuns({ params: { sort: '-created_at', per_page: 1 } }));

    combineLatest([
      this.store.select(selectDailyLeadRuns),
      this.store.select(selectDailyLeadRunsLoading),
    ]).pipe(
      takeUntil(this.destroy$),
      filter(([, runsLoading]) => !runsLoading), // Only act after API responds
    ).subscribe(([runs]) => {
      const latestRun = runs?.[0];
      const wasInProgress = this.runInProgress;
      this.runInProgress = !!latestRun && !latestRun.completed_at;

      if (!this.runsChecked) {
        this.runsChecked = true;
        // First load: fetch leads only if no run is in progress
        if (!this.runInProgress) {
          this.loading = true; // Prevent brief flash of empty state before NgRx updates
          this.store.dispatch(loadDailyLeads({ params: { filter: { status: 'new' }, sort: '-created_at' } }));
        }
      } else if (wasInProgress && !this.runInProgress) {
        // Run just completed — reload leads
        this.hasGenerated = false;
        this.loading = true;
        this.setFilter(this.activeFilter);
      }
    });

    // Poll every 5s while a run is in progress
    interval(5000).pipe(
      takeUntil(this.destroy$),
      filter(() => this.runInProgress),
    ).subscribe(() => {
      this.store.dispatch(loadDailyLeadRuns({ params: { sort: '-created_at', per_page: 1 } }));
    });

    // Load notifications for tab count
    this.store.dispatch(loadNotifications({ params: {} }));

    this.store.select(selectNotifications).pipe(takeUntil(this.destroy$)).subscribe(notifications => {
      this.notifications = (notifications || []).filter(n => !n.read_at);
    });

    this.store.select(selectNotificationsMeta).pipe(takeUntil(this.destroy$)).subscribe(meta => {
      this.notificationsMeta = meta;
    });

    this.store.select(selectNotificationsUnreadCount).pipe(takeUntil(this.destroy$)).subscribe(count => {
      this.notificationsUnreadCount = count;
    });

    this.store.select(selectMarkingAllRead).pipe(takeUntil(this.destroy$)).subscribe(marking => {
      this.markingAllRead = marking;
    });

    this.store.select(selectIcps).pipe(takeUntil(this.destroy$)).subscribe(icps => {
      this.icps = icps || [];
    });

    this.store.select(selectIcpsLoading).pipe(takeUntil(this.destroy$)).subscribe(loading => {
      this.icpsLoading = loading;
    });

    this.store.select(selectAuthUser).pipe(takeUntil(this.destroy$)).subscribe(user => {
      this.user = user;
    });

    // Listen for WebSocket event when report finishes generating
    this.sharedService.eventEmitter.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (event.type === EventTypes.SHOW_REPORT_BANNER && this.generatingProfileId) {
        const reportId = event.data.report_id;
        this.generatedReports.set(this.generatingProfileId, reportId);
        this.generatingProfileId = null;
      }
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.searchSuccessSub?.unsubscribe();
    this.searchFailureSub?.unsubscribe();
  }

  get filteredLeads(): Lead[] {
    let leads = this.leads;
    if (this.activeFilter === 'new') {
      leads = leads.filter(l => this.isCreatedToday(l.createdAt));
    }
    return this.sortLeads(leads);
  }

  get tabCounts(): { all: number; new: number; notifications: number } {
    const total = this.meta?.total ?? this.leads.length;
    return {
      all: this.activeFilter === 'all' ? total : this.leads.length,
      new: this.activeFilter === 'new' ? total : this.leads.filter(l => l.status === 'new').length,
      notifications: this.notificationsMeta?.total ?? this.notifications.length,
    };
  }

  setFilter(tab: FilterTab): void {
    this.activeFilter = tab;
    if (tab === 'notifications') {
      this.store.dispatch(loadNotifications({ params: {} }));
      return;
    }
    const params: any = { sort: '-created_at' };
    if (tab === 'new') {
      params.filter = { status: 'new' };
    }
    this.store.dispatch(loadDailyLeads({ params }));
  }

  onPageChanged(page: number): void {
    const params: any = { sort: '-created_at', page, per_page: this.meta?.per_page ?? 15 };
    if (this.activeFilter === 'new') {
      params.filter = { status: 'new' };
    }
    this.store.dispatch(loadDailyLeads({ params }));
  }

  onPerPageChanged(perPage: number): void {
    const params: any = { sort: '-created_at', page: 1, per_page: perPage };
    if (this.activeFilter === 'new') {
      params.filter = { status: 'new' };
    }
    this.store.dispatch(loadDailyLeads({ params }));
  }

  onSortChange(sort: SortOption): void {
    this.sortBy = sort;
  }

  trackByLeadId(index: number, lead: Lead): string {
    return lead.id;
  }

  onBookmarkToggle(lead: Lead): void {
    // Placeholder for API call
  }

  onFeedbackGiven(event: { lead: Lead; type: 'positive' | 'negative' }): void {
    // Placeholder for API call
  }

  onViewProfile(lead: Lead): void {
    // If report already generated, navigate to it
    const existingReportId = this.generatedReports.get(lead.id);
    if (existingReportId) {
      this.router.navigate(['/report', existingReportId]);
      return;
    }

    if (!lead.linkedinUrl) return;

    this.generatingProfileId = lead.id;
    this.generatingLinkedinUrl = lead.linkedinUrl;

    this.reportBannerService.forceDisableBulkUploadMode();
    this.sharedService.emitEvent(EventTypes.SHOW_LOADING_REPORT_BANNER, { message: 'Loading Report Banner' });

    this.store.dispatch(newSearch({
      user_id: this.user?.id,
      linkedin_profile: lead.linkedinUrl,
    }));

    // Clean up previous subscriptions
    this.searchSuccessSub?.unsubscribe();
    this.searchFailureSub?.unsubscribe();

    // Listen for search success
    this.searchSuccessSub = this.store.select(selectNewSearchSuccess).pipe(
      filter(response => response !== null),
      filter(response => response.search?.linkedin_profile === this.generatingLinkedinUrl),
      take(1),
    ).subscribe(response => {
      if (response.search && response.search.search_status_id) {
        const reportId = response.search.id;

        if (response.search.search_status_id === 3) {
          // Report already complete — close banner and mark as ready
          this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });
          this.generatedReports.set(lead.id, reportId);
          this.generatingProfileId = null;
        } else {
          // Report is still processing — store the report ID
          // The WebSocket SHOW_REPORT_BANNER event will mark it as complete
          this.generatedReports.set(lead.id, reportId);
        }
      }
    });

    // Listen for search failure
    this.searchFailureSub = this.store.select(selectNewSearchFailure).pipe(
      filter(error => error !== null),
      take(1),
    ).subscribe(() => {
      this.sharedService.emitEvent(EventTypes.CLOSE_REPORT_BANNER, { message: 'Close Report Banner' });
      this.generatingProfileId = null;
      this.generatingLinkedinUrl = null;
    });
  }

  onOpenIcpModal(): void {
    this.showIcpModal = true;
    this.store.dispatch(IcpActions.loadIcps({ page: 1, perPage: 100 }));
  }

  onCloseIcpModal(): void {
    this.showIcpModal = false;
  }

  onGenerateLeads(icpId: number): void {
    this.showIcpModal = false;
    this.selectedIcpId = icpId;
    this.icpAdjustmentNotification = null;
    // Only show empty state processing when there are no existing leads
    if (this.filteredLeads.length === 0) {
      this.runInProgress = true;
    }
    this.store.dispatch(generateDailyLeads({ count: 5, icp_id: icpId }));
    this.store.dispatch(loadNotifications({ params: {} }));
    this.leadsService.getIcpAdjustmentNotification().pipe(take(1)).subscribe(n => {
      this.icpAdjustmentNotification = n;
    });
  }

  onRefreshLeads(): void {
    this.hasGenerated = false;
    this.icpAdjustmentNotification = null;
    this.activeFilter = 'all';
    this.loading = true;
    this.store.dispatch(loadDailyLeads({ params: { sort: '-created_at' } }));
    this.store.dispatch(loadDailyLeadRuns({ params: { sort: '-created_at', per_page: 1 } }));
    this.store.dispatch(loadNotifications({ params: {} }));
  }

  onMarkAllRead(): void {
    this.store.dispatch(markAllNotificationsRead());
  }

  onSendEmail(lead: Lead): void {
    // Placeholder for email action
  }

  onViewDetails(lead: Lead): void {
    this.showDetailModal = true;
    this.store.dispatch(loadDailyLeadDetail({ id: lead.id }));
  }

  onCloseDetailModal(): void {
    this.showDetailModal = false;
    this.store.dispatch(clearDailyLeadDetail());
  }

  private sortLeads(leads: Lead[]): Lead[] {
    return [...leads].sort((a, b) => {
      switch (this.sortBy) {
        case 'newest':
          return parseInt(b.id) - parseInt(a.id);
        case 'oldest':
          return parseInt(a.id) - parseInt(b.id);
        default:
          return 0;
      }
    });
  }

  private isCreatedToday(createdAt?: string): boolean {
    if (!createdAt) return false;
    const created = new Date(createdAt);
    const today = new Date();
    return created.getFullYear() === today.getFullYear()
      && created.getMonth() === today.getMonth()
      && created.getDate() === today.getDate();
  }

  private mapDailyLeadToLead(dl: DailyLead, index: number): Lead {
    const statusMap: Record<string, Lead['status']> = {
      'new': 'new',
      'viewed': 'new',
      'contacted': 'contacted',
      'opened': 'opened',
      'replied': 'replied',
    };

    return {
      id: dl.id,
      firstName: dl.person_first_name || dl.person_name?.split(' ')[0] || '',
      lastName: dl.person_last_name || dl.person_name?.split(' ').slice(1).join(' ') || '',
      jobTitle: dl.person_title || '',
      company: dl.company_name || '',
      location: dl.person_location || dl.company_location || '',
      avatarColor: AVATAR_COLORS[index % AVATAR_COLORS.length],
      status: statusMap[dl.status] || 'new',
      matchPercentage: dl.icp_score || 0,
      matchTier: 'Basic',
      isBookmarked: false,
      feedback: null,
      whyThisLead: dl.why_this_lead || undefined,
      linkedinUrl: dl.person_linkedin_url || null,
      photoUrl: dl.apollo_person_data?.photo_url || null,
      departments: dl.apollo_person_data?.departments || [],
      companyLogoUrl: dl.apollo_company_data?.logo_url || null,
      createdAt: dl.created_at,
    };
  }
}
